import { useCallback, useEffect, useRef, useState } from "react";

type UseCountdownProps = {
  countdownDurationInSeconds: number;
  refreshIntervalInSeconds?: number;
  disabled?: boolean;
  onCountdownFinished: () => void;
};

/**
 * Custom hook to manage a countdown timer.
 *
 * @param {number} countdownDurationInSeconds - The initial duration of the countdown in seconds.
 * @param {number} refreshIntervalInSeconds - The interval at which the counter refresh in seconds.
 * @param {boolean} disabled - Whether the countdown is disabled/paused
 * @param {() => void} onCountdownFinished - Callback function to be invoked when the countdown is reached.
 * @returns {{ timeLeftInSeconds: number, resetCountdown: () => void }} - An object containing the time left in the countdown and a reset function.
 */
export const useCountdown = ({
  countdownDurationInSeconds,
  refreshIntervalInSeconds = 1,
  disabled = false,
  onCountdownFinished
}: UseCountdownProps) => {
  const [timeLeftInSeconds, setTimeLeftInSeconds] = useState(
    countdownDurationInSeconds
  );

  const onCountdownFinishedRef = useRef(onCountdownFinished);

  const resetCountdown = useCallback(() => {
    setTimeLeftInSeconds(countdownDurationInSeconds);
  }, [countdownDurationInSeconds]);

  useEffect(() => {
    // saving reference of onCountdownFinished, to not cause re-renders when onCountdownFinished updates
    onCountdownFinishedRef.current = onCountdownFinished;
  }, [onCountdownFinished]);

  useEffect(() => {
    // Don't start countdown if disabled
    if (disabled) {
      resetCountdown();
      return;
    }

    if (timeLeftInSeconds <= 0) {
      onCountdownFinishedRef.current();
      return;
    }

    const timer = setInterval(() => {
      setTimeLeftInSeconds((prev) => prev - refreshIntervalInSeconds);
    }, refreshIntervalInSeconds * 1000);

    return () => {
      clearInterval(timer);
    };
  }, [resetCountdown, timeLeftInSeconds, refreshIntervalInSeconds, disabled]);

  return { timeLeftInSeconds, resetCountdown };
};
